<template>
  <div class="agent-plaza">
    <!-- 头部：标题 + 搜索框 -->
    <div class="plaza-header">
      <h3 class="plaza-title">智能体广场</h3>
      <div class="plaza-search">
        <input
          v-model="searchName"
          type="text"
          placeholder="搜索智能体名称"
          @keyup.enter="handleSearch"
        />
        <i v-if="searchName" class="el-icon-circle-close" @click="clearSearch"></i>
      </div>
    </div>

    <!-- 卡片网格 -->
    <div
      ref="plazaGrid"
      v-loading="loading"
      class="plaza-grid"
      @scroll="handleScroll"
    >
      <article
        v-for="item in agentList"
        :key="item.id"
        class="plaza-card"
        @click="handleSelect(item)"
      >
        <!-- ===== 市场智能体卡片（source=agent，样式对齐数字人广场） ===== -->
        <template v-if="isMarketAgent(item)">
          <!-- 安装中遮罩：席位校验通过后展示，模拟安装进度（接口成功后 100%） -->
          <div v-if="installingKey === item.id" class="plaza-installing">
            <div class="plaza-installing__spinner"></div>
            <div class="plaza-installing__text">安装中...</div>
            <div class="plaza-installing__bar">
              <div
                class="plaza-installing__bar-fill"
                :style="{ width: installProgress + '%' }"
              ></div>
            </div>
          </div>

          <!-- 已订阅角标（subscribed === 3） -->
          <span v-if="item.subscribeState === 2" class="plaza-foot-installed">
            <img :src="buyedIcon" alt="已订阅" />
          </span>

          <!-- 卡片头部：头像 + 名称 + 市场分类/交付方式标签 -->
          <div class="plaza-card-head">
            <div class="plaza-avatar">
              <img v-if="item.logo" :src="item.logo" alt="" />
              <i v-else class="el-icon-menu"></i>
            </div>
            <div class="plaza-card-main">
              <div class="plaza-title-line">
                <div class="plaza-card-title" :title="item.name">{{ item.name }}</div>
              </div>
              <div class="plaza-tag-line">
                <span
                  v-if="marketCategoryName(item.marketCategoryType)"
                  class="plaza-category"
                >{{ marketCategoryName(item.marketCategoryType) }}</span>
                <span v-if="item.deliveryName" class="plaza-delivery">{{ item.deliveryName }}</span>
              </div>
            </div>
          </div>

          <!-- 描述 -->
          <div class="plaza-card-desc" :title="item.desc || null">{{ item.desc }}</div>

          <!-- 底部：免费标签 + 订阅/试用留言按钮（已订阅不展示价格和操作按钮） -->
          <div class="plaza-foot">
            <template v-if="item.subscribeState !== 2">
              <span v-if="!(item.priceNum > 0)" class="plaza-price-tag-free">免费</span>
              <button
                type="button"
                class="plaza-action-btn"
                :disabled="!!subscribingId"
                @click.stop="handleAction(item)"
              >{{ subscribingId === item.id ? '订阅中…' : (item.subscribeState === 1 ? '订阅' : '试用留言') }}</button>
            </template>
          </div>
        </template>

        <!-- ===== 普通智能体卡片 ===== -->
        <template v-else>
          <!-- 右上角使用按钮 (hover 显示) -->
          <button
            type="button"
            class="plaza-use-btn"
            @click.stop="handleUse(item)"
          >使用</button>

          <!-- 卡片头部：头像 + 名称 + 标签 -->
          <div class="plaza-card-head">
            <div class="plaza-avatar">
              <img v-if="item.logo" :src="item.logo" alt="" />
              <i v-else class="el-icon-menu"></i>
            </div>
            <div class="plaza-card-main">
              <div class="plaza-title-line">
                <div class="plaza-card-title" :title="item.displayName || item.name">
                  {{ item.displayName || item.name }}
                </div>
              </div>
              <span class="plaza-category">{{ item.type || '智能体' }}</span>
              <span class="plaza-version">v{{ item.version || '1.0.0' }}</span>
            </div>
          </div>

          <!-- 描述 -->
          <div class="plaza-card-desc">{{ item.desc || '&nbsp;' }}</div>

          <!-- 底部：统计 -->
          <div class="plaza-card-foot">
            <span class="plaza-stat">
              <img :src="require(`@/assets/imgs/workbench/zan.png`)" alt="" class="plaza-stat-icon" />{{ item.likes || 0 }}
            </span>
            <span class="plaza-stat">
              <img :src="require(`@/assets/imgs/workbench/dianji.png`)" alt="" class="plaza-stat-icon" />{{ item.usageCount || 0 }}
            </span>
          </div>
        </template>
      </article>

      <div v-if="loadingMore" class="plaza-loading-more">
        <i class="el-icon-loading"></i> 加载更多...
      </div>
      <div v-if="agentList.length === 0 && !loading" class="plaza-empty">
        <i class="el-icon-folder-opened"></i>
        <span>暂无智能体</span>
      </div>
    </div>

    <!-- 市场智能体（source=agent）试用留言弹窗：复用智能体广场页面的留言弹窗 -->
    <trial-apply-dialog v-model="applyVisible" :source="5" />

    <!-- 席位校验未通过弹窗：错误图标 + 标题 + 说明，内容整体居中 -->
    <el-dialog
      :visible.sync="seatLimitVisible"
      custom-class="seat-limit-dialog"
      append-to-body
      width="440px"
      :show-close="false"
    >
      <div class="seat-limit">
        <img class="seat-limit__icon" :src="warningIcon" alt="席位已达上限" />
        <div class="seat-limit__title">企业席位使用已达上限</div>
        <div class="seat-limit__desc">{{ seatLimitMessage }}</div>
        <button
          type="button"
          class="seat-limit__btn"
          @click="handleApplyQuota"
        >申请配额</button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAgentPlazaApi,
  getAssistantClientCategoriesApi,
  getWorkspaceId
} from '@/api/agentAuth'
import { createAgent, getAgentSkills, saveAgentSkills } from '@/api/digitalHuman'
import { copyAgentConfig } from '@/api/sphereAgentConfig'
import { validateSphereUserSeat, isSphereSeatValid } from '@/api/sphereUser'
import buyedIcon from '@/assets/imgs/workbench/buyed.png'
import warningIcon from '@/assets/imgs/warning.png'
import TrialApplyDialog from '@/components/common/TrialApplyDialog'

// 交付方式：deploy-部署实施，out_of_box-开箱即用
function getDeliveryName(deliverMode) {
  const mode = String(deliverMode === undefined || deliverMode === null ? '' : deliverMode).trim()
  if (mode === 'deploy') return '部署实施'
  if (mode === 'out_of_box') return '开箱即用'
  return ''
}

// 价格归一化：非正数 / 无法解析均视为免费
function getPrice(raw) {
  if (raw === null || raw === undefined || raw === '') return 0
  const num = Number(raw)
  if (!Number.isNaN(num) && num > 0) return num
  return 0
}

// subscribed 枚举（当前租户对市场智能体的状态）：
//   1 - 未购买 2 - 可订阅（可直接订阅） 3 - 已订阅
// 内部 subscribeState：0-可留言 1-可订阅 2-已订阅
// 前端规则：未购买且免费（price<=0）的智能体视为可直接订阅（对齐数字人广场免费开箱即用），
//          付费未购买仍仅可留言；后端补齐 subscribed 字段后此逻辑保持兼容
function resolveSubscribeState(raw) {
  const num = Number(raw && raw.subscribed)
  if (num === 2) return 1
  if (num === 3) return 2
  if (num === 1 && getPrice(raw && raw.price) === 0) return 1
  return 0
}

export default {
  name: 'AgentPlaza',
  components: { TrialApplyDialog },
  data() {
    return {
      buyedIcon,
      warningIcon,
      agentList: [],
      total: 0,
      pageNum: 1,
      pageSize: 12,
      loadingMore: false,
      noMore: false,
      searchName: '',
      loading: false,
      error: '',
      // 市场智能体（source=agent）分类映射与订阅流程状态
      categories: [],
      applyVisible: false,
      subscribingId: '',
      seatLimitVisible: false,
      seatLimitMessage: '',
      // 「安装中」进度：installingKey 为当前安装（订阅）中的卡片 id，installProgress 为 0-100
      installingKey: '',
      installProgress: 0
    }
  },
  mounted() {
    this.fetchList()
    this.loadCategories()
  },
  beforeDestroy() {
    this.stopInstallProgress()
  },
  methods: {
    // 是否市场智能体卡片（source=agent，样式与按钮对齐数字人广场）
    isMarketAgent(item) {
      return String((item && item.source) || '') === 'agent'
    },
    // 市场一级分类 ID → 分类名（用于卡片标签展示）
    marketCategoryName(id) {
      if (id === '' || id === null || id === undefined) return ''
      const found = (this.categories || []).find(
        (cat) => String(cat.id) === String(id)
      )
      return (found && found.label) || ''
    },
    // 加载市场一级分类（仅用于 source=agent 卡片标签展示，失败静默不影响列表）
    async loadCategories() {
      try {
        const res = await getAssistantClientCategoriesApi()
        const payload = res && res.data !== undefined ? res.data : res
        const rawList = Array.isArray(payload)
          ? payload
          : (payload && (payload.rows || payload.list || payload.records || payload.data)) || []
        const normalized = rawList
          .map((raw, index) => {
            if (!raw) return null
            if (typeof raw === 'string' || typeof raw === 'number') {
              return { id: String(raw), label: String(raw), sort: index }
            }
            if (typeof raw !== 'object') return null
            const rawId = raw.id !== undefined && raw.id !== null && raw.id !== ''
              ? raw.id
              : (raw.categoryId !== undefined ? raw.categoryId : raw.marketCategoryType)
            if (rawId === '' || rawId === null || rawId === undefined) return null
            const label = raw.categoryName || raw.name || raw.label || raw.title || rawId
            const sortNum = Number(raw.sort !== undefined ? raw.sort : 0)
            return {
              id: String(rawId),
              label: String(label),
              sort: Number.isFinite(sortNum) ? sortNum : 0
            }
          })
          .filter(Boolean)
        normalized.sort((a, b) => a.sort - b.sort)
        this.categories = normalized
      } catch (e) {
        console.warn('[AgentPlaza] 加载市场分类失败:', e)
      }
    },
    clearSearch() {
      this.searchName = ''
      this.handleSearch()
    },
    async fetchList(append) {
      if (append) {
        this.loadingMore = true
      } else {
        this.loading = true
      }
      this.error = ''
      try {
        const params = { pageNum: this.pageNum, pageSize: this.pageSize }
        if (this.searchName) params.name = this.searchName
        const res = await getAgentPlazaApi(params)
        const body = res.data || res
        const data = body.data || body
        const rows = data.rows || []
        // source=agent 的行做展示字段归一化（交付方式/价格/订阅状态），其余行原样保留
        const normalized = rows.map((raw) => this.normalizeAgentRow(raw))
        if (append) {
          this.agentList = [...this.agentList, ...normalized]
        } else {
          this.agentList = normalized
        }
        this.total = data.total || 0
        this.noMore = rows.length < this.pageSize
      } catch (e) {
        this.error = '加载失败: ' + (e.message || '未知错误')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    // 市场智能体行归一化：补充 deliveryName / priceNum / subscribeState
    normalizeAgentRow(raw) {
      const item = raw || {}
      if (String(item.source || '') !== 'agent') return item
      return {
        ...item,
        deliveryName: getDeliveryName(item.deliverMode),
        priceNum: getPrice(item.price),
        subscribeState: resolveSubscribeState(item)
      }
    },
    handleScroll() {
      const el = this.$refs.plazaGrid
      if (!el || this.loadingMore || this.noMore) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
        this.pageNum++
        this.fetchList(true)
      }
    },
    handleSearch() {
      this.pageNum = 1
      this.fetchList()
    },
    handleSelect(item) {
      // 市场智能体卡片与数字人广场一致：点击卡片本体不触发 select
      if (this.isMarketAgent(item)) return
      this.$emit('select', item)
    },
    handleUse(item) {
      this.$emit('use', item)
    },
    // 市场智能体底部按钮：可订阅走订阅流程，其余（可留言）弹出留言弹窗
    handleAction(item) {
      if (item.subscribeState !== 1) {
        this.applyVisible = true
        return
      }
      this.subscribeAgent(item)
    },
    // 订阅流程（完整复用数字人广场）：席位校验 -> 安装进度 -> 取技能 -> 以模板创建实例 -> 复制配置 -> 绑定技能
    async subscribeAgent(item) {
      if (this.subscribingId) return
      // assistantId 即 claw 侧真实智能体编号；缺失时回退列表 id
      const sourceAgentId = item.assistantId || item.id
      if (!sourceAgentId) {
        this.$message.error('当前智能体缺少编号，无法订阅')
        return
      }
      this.subscribingId = item.id
      let workspaceId = ''
      try {
        workspaceId = getWorkspaceId()
      } catch (e) {
        workspaceId = ''
      }
      try {
        // 0. 可订阅的智能体：订阅前先调用席位校验接口，校验通过后才继续订阅，不通过直接提示
        let seatData = null
        try {
          const seatRes = await validateSphereUserSeat()
          seatData = (seatRes && seatRes.data) || seatRes || {}
        } catch (e) {
          console.warn('[AgentPlaza] 席位校验失败:', e)
          this.$message.error(
            '席位校验失败：' + ((e && e.message) || '请稍后重试')
          )
          return
        }
        if (!isSphereSeatValid(seatData)) {
          this.showSeatLimitDialog(seatData)
          return
        }

        // 席位校验通过：卡片切换为「安装中」，模拟安装进度
        this.startInstallProgress(item.id)

        // 1. 获取该智能体拥有的全部 skill
        const skillsRes = await getAgentSkills(sourceAgentId, workspaceId || undefined)
        const skillList = skillsRes?.data || skillsRes || []
        const skillIds = skillList
          .map((s) =>
            s && typeof s === 'object'
              ? s.skillId ?? s.clawSkillId ?? s.id
              : s
          )
          .filter((v) => v !== '' && v !== undefined && v !== null)
          .map(String)

        // 2. 以当前智能体为模板创建实例（name/icon/description 取当前智能体值）
        const payload = {
          name: item.name,
          agentType: 'react',
          templateId: String(sourceAgentId),
          icon: item.logo || undefined,
          tags: item.tags || undefined,
          description: item.desc || undefined,
          maxIterations: 100,
          toolsDisabled: false,
          sortOrder: 0,
          enabled: true
        }
        const createRes = await createAgent(payload, workspaceId || undefined)
        const newAgentId = createRes?.data?.id || createRes?.id
        if (!newAgentId) {
          this.$message.error('创建智能体失败')
          return
        }

        // 创建成功后立即复制模板配置（子菜单/快捷入口）到新实例，失败不阻断订阅流程
        try {
          await copyAgentConfig({
            sourceAgentId: payload.templateId,
            targetAgentId: newAgentId
          })
        } catch (copyErr) {
          console.warn('[AgentPlaza] 复制智能体配置失败:', copyErr)
        }

        // 3. 把 skill 以字符串数组形式绑定到新创建的实例
        if (skillIds.length) {
          await saveAgentSkills(newAgentId, skillIds, workspaceId || undefined)
        }
        // 接口全部返回成功：进度补满 100%，短暂停留后收起遮罩
        await this.completeInstallProgress()
        this.$message.success('订阅成功')
        this.resetInstallState()
        // 订阅会按模板创建实例：刷新 agentList 后，
        // 左侧菜单树（数字人/智能体目录）会随 store 状态自动更新
        this.$store
          .dispatch('user/refreshAgentList')
          .catch((e) => {
            console.warn('[AgentPlaza] 订阅后刷新左侧菜单树失败:', e)
          })
        await this.fetchList()
      } catch (err) {
        console.warn('[AgentPlaza] 订阅失败:', err)
        this.$message.error((err && err.message) || '订阅失败，请稍后重试')
      } finally {
        this.resetInstallState()
        this.subscribingId = ''
      }
    },
    // ===== 「安装中」进度动画 =====
    // 席位校验通过后开始：约 10s 内线性推进到 99%，接口全部成功后再补满 100%
    startInstallProgress(key) {
      this.stopInstallProgress()
      this.installingKey = key
      this.installProgress = 0
      this._installDuration = 10000
      this._installStartedAt = Date.now()
      this._installTimer = setInterval(() => {
        const elapsed = Date.now() - this._installStartedAt
        const ratio = Math.min(elapsed / this._installDuration, 1)
        // 10s 内线性 0 -> 99%；动画/接口未完成前停在 99% 等待
        this.installProgress = Math.min(Math.round(ratio * 99), 99)
      }, 200)
    },
    stopInstallProgress() {
      if (this._installTimer) {
        clearInterval(this._installTimer)
        this._installTimer = null
      }
    },
    // 接口全部成功：立即补满 100%（接口提前返回则提前结束），停留 400ms 让用户看到满格
    async completeInstallProgress() {
      // 未处于安装态（异常路径兜底）：直接返回
      if (!this.installingKey) return
      this.stopInstallProgress()
      this.installProgress = 100
      this._installStartedAt = 0
      await new Promise((resolve) => setTimeout(resolve, 400))
    },
    resetInstallState() {
      this.stopInstallProgress()
      this.installingKey = ''
      this.installProgress = 0
      this._installStartedAt = 0
    },
    // 席位校验未通过：弹窗展示接口返回的 message
    showSeatLimitDialog(seatData) {
      this.seatLimitMessage = (seatData && seatData.message) || ''
      this.seatLimitVisible = true
    },
    // 席位上限弹窗点击「申请配额」：关闭席位弹窗，打开留言/申请弹窗
    handleApplyQuota() {
      this.seatLimitVisible = false
      this.applyVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.agent-plaza {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  color: #333;
}

/* ===== 头部 ===== */
.plaza-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-shrink: 0;
}

.plaza-title {
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  color: #333333;
  letter-spacing: 0;
  line-height: 32px;
}

/* 搜索框 —— 对齐 dh-search */
.plaza-search {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 221px;
  height: 32px;
  background: #FFFFFF;
  border: 1px solid #DDDDDD;
  border-radius: 4px;
  color: #333;
  font-size: 14px;
  padding: 6px 12px;
  box-sizing: border-box;

  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 14px;
    color: #333;

    &::placeholder {
      color: #DDDDDD;
    }
  }

  i {
    flex: none;
    cursor: pointer;
    color: #999;
    font-size: 14px;
    margin-left: 6px;
    &:hover {
      color: #666;
    }
  }
}

/* ===== 错误 ===== */
.plaza-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 80px 0;
  color: #f56c6c;
  font-size: 14px;
}

/* ===== 网格 —— 对齐 dh-grid ===== */
.plaza-grid {
  padding: 0 10px 0 0;
  background: transparent;
  border-radius: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  flex: 1;
  overflow-y: auto;
  align-content: start;
  align-items: stretch;
}

/* ===== 卡片 —— 对齐 dh-card ===== */
.plaza-card {
  position: relative;
  min-height: 187px;
  overflow: hidden;
  background: #FFFFFF;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  cursor: pointer;
  padding: 20px 20px;
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;

  &:hover {
    box-shadow: 0 0 10px 0 #4a5ff333;
    // transform: translateY(-2px);

    .plaza-use-btn {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

/* 使用按钮 —— 对齐 dh-foot-sub */
.plaza-use-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  border: 0;
  background-image: linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%);
  border-radius: 2px;
  color: #FFFFFF;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  opacity: 0;
  padding: 3px 12px;
  transform: translateY(-4px);
  transition: opacity .18s ease, transform .18s ease, background .18s ease;
  white-space: nowrap;
}

/* ===== 卡片头部 —— 对齐 dh-card-head ===== */
.plaza-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

/* 头像 —— 对齐 dh-avatar */
.plaza-avatar {
  display: inline-flex;
  width: 56px;
  height: 56px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: linear-gradient(135deg, #EAF2FF, #C7DBFF);
  color: #2563EB;
  font-size: 21px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .65);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  i {
    font-size: 22px;
    color: #2563EB;
  }
}

.plaza-card-main {
  min-width: 0;
  flex: 1;
}

.plaza-title-line {
  display: flex;
  min-width: 0;
  align-items: center;
  margin-bottom: 13px;
  gap: 8px;
}

/* 标题 —— 对齐 dh-name */
.plaza-card-title {
  overflow: hidden;
  font-weight: 600;
  font-size: 16px;
  color: #333333;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 类型标签 —— 对齐 dh-category */
.plaza-category {
  border: 1px solid #DDDDDD;
  border-radius: 2px;
  font-size: 12px;
  color: #333333;
  letter-spacing: 0;
  line-height: 16px;
  padding: 3px 8px;
}

/* 版本标签 —— 对齐 dh-version */
.plaza-version {
  border: 1px solid #E4E8FC;
  background: #E4E8FC;
  border-radius: 2px;
  font-size: 12px;
  color: #4A5FF3;
  line-height: 12px;
  margin-left: 12px;
  padding: 3px 8px;
}

/* 描述 —— 对齐 dh-desc */
.plaza-card-desc {
  display: -webkit-box;
  min-height: 40px;
  margin-top: 14px;
  overflow: hidden;
  font-weight: 400;
  font-size: 12px;
  color: #808080;
  line-height: 20px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* ===== 底部统计 —— 对齐 dh-foot + dh-download ===== */
.plaza-card-foot {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 15px;
}

.plaza-stat-icon {
      width: 14px;
      height: 14px;
      flex: none;
    }

    .plaza-stat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
  font-size: 12px;
  color: #808080;
  line-height: 16px;

  i {
    font-size: 13px;
  }
}

/* ===== 空状态 & 加载更多 ===== */
.plaza-empty {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 0;
  color: #c0c4cc;
  font-size: 14px;
  i {
    font-size: 48px;
  }
}

.plaza-loading-more {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 16px 0;
  color: #8f959e;
  font-size: 13px;
}

/* ===== 市场智能体卡片（source=agent，对齐数字人广场 dh-card） ===== */

/* 分类 + 交付方式标签行 */
.plaza-tag-line {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

/* 交付方式标签 —— 对齐 dh-delivery */
.plaza-delivery {
  border: 1px solid #E4E8FC;
  background: #E4E8FC;
  border-radius: 2px;
  font-size: 12px;
  color: #4A5FF3;
  line-height: 12px;
  padding: 4px 8px;
}

/* 底部 —— 免费标签 + 操作按钮（右对齐） */
.plaza-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-height: 24px;
  margin-top: 15px;
}

/* 免费标签 —— 对齐 dh-price-tag-free */
.plaza-price-tag-free {
  background: #EDF1FB;
  border: 1px solid #4A5FF3;
  border-radius: 2px;
  padding: 2px 8px;
  font-size: 12px;
  color: #4A5FF3;
  letter-spacing: 0;
  text-align: center;
  line-height: 18px;
}

/* 订阅 / 试用留言按钮 —— 对齐 dh-action-btn */
.plaza-action-btn {
  border: 0;
  background-image: linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%);
  border-radius: 2px;
  color: #FFFFFF;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding: 4px 14px;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

/* 已订阅角标 —— 对齐 dh-foot-installed */
.plaza-foot-installed {
  position: absolute;
  top: 0;
  right: 0;
  width: 68px;
  height: 24px;

  img {
    width: 100%;
    height: 100%;
  }
}

/* 「安装中」遮罩：卡片内容模糊淡化，居中展示 spinner + 文案 + 进度条 */
.plaza-installing {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, .82);
  backdrop-filter: blur(3px);
}

.plaza-installing__spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #E3E8F7;
  border-top-color: #4A5FF3;
  animation: plaza-installing-spin .8s linear infinite;
}

.plaza-installing__text {
  font-size: 14px;
  font-weight: 600;
  color: #333333;
}

.plaza-installing__bar {
  width: 62%;
  height: 6px;
  border-radius: 999px;
  background: #E4E8F5;
  overflow: hidden;
}

.plaza-installing__bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #4A5FF3 0%, #4E86FF 100%);
  transition: width .2s linear;
}

@keyframes plaza-installing-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
.agent-plaza-panel .plaza-grid {
  &::-webkit-scrollbar {
    width: 6px !important;
    height: 6px !important;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #FFFFFF !important;
    border-radius: 8px !important;
    box-shadow: none !important;
  }
}

/* 席位校验未通过弹窗（append-to-body，弹窗外壳需用非 scoped 样式） */
.seat-limit-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 36px 32px 28px;
  }
}

.seat-limit {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.seat-limit__icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.seat-limit__title {
  margin-top: 16px;
  font-size: 18px;
  font-weight: 600;
  color: #1F2A44;
  line-height: 26px;
}

.seat-limit__desc {
  margin-top: 12px;
  font-size: 14px;
  color: #64748B;
  line-height: 22px;
  word-break: break-all;
}

.seat-limit__btn {
  margin-top: 24px;
  width: 120px;
  height: 38px;
  border: 0;
  border-radius: 19px;
  background: linear-gradient(135deg, #155EEF, #0B63E5);
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: .9;
  }
}
</style>
